import Chip from '@mui/material/Chip'
import { alpha, useTheme } from '@mui/material/styles'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import { stateOf } from '../Staff/MicRail'
import { CAUSE_HELP, causeLabel } from '../StreamIngest/vocabulary'

export const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

// What a list names an account by: the name, else the username, else the
// id's tail (a guest that never chose either).
export const displayName = (user) =>
  user.full_name ||
  (user.username ? `@${user.username}` : `…${user.user_id.slice(-6)}`)

// Audio as the page says it: minutes under an hour, then hours.
export const fmtStreamed = (ms) => {
  if (!ms) return '0 h'
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`
  const hours = ms / 3_600_000
  return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)} h`
}

// How the latest stop was named, on the heartbeat lease. The phone's words
// are the vocabulary its spans close with (`seal_cause`), plus three only the
// lease carries; `dirty` is the server's, never the phone's. Open, like the
// causes: an unknown word still shows, as itself.
const STOP_HELP = {
  ...CAUSE_HELP,
  will_terminate: 'iOS told the app it was about to close.',
  stalled: 'The microphone stopped delivering audio while the app ran.',
  dirty:
    'The phone stopped beating without saying why: the watchdog’s verdict, once the beats were overdue. The app died, or lost the network.',
}

export const stopLabel = causeLabel

export const stopHelp = (reason) =>
  STOP_HELP[reason] || `The phone said "${causeLabel(reason)}".`

// The microphone as the staff rail reads it, as a chip with its word: live (a
// beat inside the watchdog grace), off, or never beaten at all.
export const MicChip = ({ presence }) => {
  const state = stateOf(presence)
  if (state === 'on')
    return (
      <Chip
        size="small"
        color="success"
        icon={<MicIcon />}
        label="mic on"
        sx={{ height: 22 }}
      />
    )
  if (state === 'off')
    return (
      <Chip
        size="small"
        variant="outlined"
        icon={<MicOffIcon />}
        label="mic off"
        sx={{ height: 22 }}
      />
    )
  return (
    <Chip
      size="small"
      variant="outlined"
      label="no heartbeat"
      sx={{ height: 22 }}
    />
  )
}

// One color per measure, the same on the overview, its rows and the day view:
// the dataviz skill's first four categorical slots, in their order, each
// mode's own steps. Checked with its validator against the pane's surface
// (#f3f3f3 light, #2d333b dark): every adjacent pair clears the color-vision
// floors in both modes; in light mode orange, aqua and yellow sit under 3:1
// against the surface, which the legend and the day-by-day table answer.
// A moment's push is a part of the moment, so it is the moment's hue, and the
// moments not pushed are that hue faded (`unpushed`): less in dark mode, where
// fading toward the surface darkens it into the background.
const SERIES_COLORS = {
  light: {
    streamed: '#2a78d6',
    moments: '#eb6834',
    comments: '#1baf7a',
    friends: '#eda100',
  },
  dark: {
    streamed: '#3987e5',
    moments: '#d95926',
    comments: '#199e70',
    friends: '#c98500',
  },
}

const UNPUSHED_OPACITY = { light: 0.4, dark: 0.6 }

export const useSeriesColors = () => {
  const theme = useTheme()
  const mode = SERIES_COLORS[theme.palette.mode] ? theme.palette.mode : 'light'
  const colors = SERIES_COLORS[mode]
  return {
    ...colors,
    unpushed: alpha(colors.moments, UNPUSHED_OPACITY[mode]),
  }
}
